import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { reloadToLatest, useAppVersion } from '../useAppVersion';
import { Card, Segmented, Spinner, useToast } from '../components/ui';
import { SchoolRunPattern } from '../components/SchoolRunPattern';
import { HouseholdAccessCard } from '../components/HouseholdAccessCard';
import { KitchenDeviceCard } from '../components/KitchenDeviceCard';
import { ShoppingDaysCard } from '../components/ShoppingDaysCard';
import { DislikesCard } from '../components/DislikesCard';
import type { Household, Language, Me, Member } from '../types';

const T = {
  en: {
    title: 'Settings',
    household: 'Household',
    name: 'Household name',
    location: 'Location',
    email: 'Shopping list email',
    fish: 'Fish meals per week (minimum)',
    autoEmail: 'Email the list automatically when the week is finalised',
    whatsapp: 'Post the list to the family WhatsApp group',
    whatsappHelp:
      'Sent when the week is finalised, alongside the email. Everyone\u2019s own '
      + 'additions in the group get merged with it.',
    staples: 'Include cupboard staples on shopping lists',
    baking: 'Baking afternoons a week',
    bakingHelp:
      'How often the goûter is something you make — cookies, a lemon cake, brownies — rather than a packet of biscuits. Set it to 0 for a week with no time in it.',
    puddings: 'Made puddings a week',
    puddingsHelp:
      'The rest are a piece of fruit or a pot of yoghurt, which is what most evenings want.',
    drinksAlcoholic: 'The evening drink is usually alcoholic',
    drinksAlcoholicHelp:
      'What the adults’ apéritif defaults to. Any single evening can say otherwise when you set the week up.',
    people: 'Who lives here',
    portion: 'Portion',
    adult: 'Adult',
    child: 'Child',
    aupair: 'Au pair',
    keto: 'Keto',
    ketoFrom: 'from',
    ketoUntil: 'until',
    ketoOn: 'Doing keto',
    ketoOff: 'Eating normally',
    ketoHelp:
      'On a shared meal, keto eaters simply leave the carbohydrate — so the rest of the dish is planned to work without it, and the rice is only bought for the people eating it.',
    add: 'Add person',
    remove: 'Remove',
    rename: 'Name',
    renameHelp:
      'Use real names. They show up everywhere — who is cooking, who is being collected, who is eating.',
    save: 'Save',
    saved: 'Saved',
    language: 'Language',
    account: 'Account',
    signOut: 'Sign out',
    signedInAs: 'Signed in as',
    device: 'This is a shared household device',
    adultOnly: 'Settings can only be changed from an adult account, not the shared tablet.',
    backup: 'Backup',
    backupWhy:
      'Your favourites, cooking history and your own Regular meals are the one thing that cannot be rebuilt from scratch. Download a copy now and again.',
    download: 'Download backup',
    restore: 'Restore from a backup',
    restored: (n: number) => `Restored ${n} meal preferences`,
    version: 'Running build',
    newerAvailable: 'a newer one is deployed:',
    reload: 'Reload',
  },
  fr: {
    title: 'Réglages',
    household: 'Foyer',
    name: 'Nom du foyer',
    location: 'Localisation',
    email: 'E-mail pour les courses',
    fish: 'Repas de poisson par semaine (minimum)',
    autoEmail: 'Envoyer la liste automatiquement',
    whatsapp: 'Publier la liste dans le groupe WhatsApp de la famille',
    whatsappHelp:
      'Envoy\u00e9e \u00e0 la finalisation de la semaine, en plus de l\u2019e-mail. Les ajouts '
      + 'de chacun dans le groupe y sont fusionn\u00e9s.',
    staples: 'Inclure les produits de base',
    baking: 'Après-midis pâtisserie par semaine',
    bakingHelp:
      'À quelle fréquence le goûter est fait maison — cookies, gâteau au citron, brownies — plutôt qu’un paquet de biscuits. Mettez 0 pour une semaine sans temps.',
    puddings: 'Desserts préparés par semaine',
    puddingsHelp:
      'Les autres sont un fruit ou un yaourt, ce qui convient à la plupart des soirs.',
    drinksAlcoholic: 'L’apéritif est en général alcoolisé',
    drinksAlcoholicHelp:
      'Le choix par défaut pour l’apéritif des adultes. Chaque soir peut en décider autrement au moment de préparer la semaine.',
    people: 'Qui habite ici',
    portion: 'Portion',
    adult: 'Adulte',
    child: 'Enfant',
    aupair: 'Au pair',
    keto: 'Kéto',
    ketoFrom: 'du',
    ketoUntil: 'au',
    ketoOn: 'En kéto',
    ketoOff: 'Alimentation normale',
    ketoHelp:
      "Lors d'un repas partagé, les personnes en kéto laissent simplement le féculent — le reste du plat est prévu pour fonctionner sans, et le riz n'est acheté que pour ceux qui en mangent.",
    add: 'Ajouter',
    remove: 'Retirer',
    rename: 'Nom',
    renameHelp:
      'Utilisez les vrais prénoms. Ils apparaissent partout : qui cuisine, qui est récupéré, qui mange.',
    save: 'Enregistrer',
    saved: 'Enregistré',
    language: 'Langue',
    account: 'Compte',
    signOut: 'Se déconnecter',
    signedInAs: 'Connecté en tant que',
    device: 'Appareil partagé du foyer',
    adultOnly: "Les réglages nécessitent un compte adulte, pas la tablette partagée.",
    backup: 'Sauvegarde',
    backupWhy:
      'Vos favoris, votre historique et vos repas habituels ne peuvent pas être régénérés. Téléchargez-les de temps en temps.',
    download: 'Télécharger la sauvegarde',
    restore: 'Restaurer une sauvegarde',
    restored: (n: number) => `Restauré : ${n} préférences`,
    version: 'Version installée',
    newerAvailable: 'une plus récente est déployée :',
    reload: 'Recharger',
  },
} as const;

export function SettingsPage({
  language,
  setLanguage,
  me,
  onSignOut,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
  me: Me;
  onSignOut: () => void;
}) {
  const t = T[language];
  const version = useAppVersion();
  const toast = useToast();
  const [household, setHousehold] = useState<Household | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [busy, setBusy] = useState(false);

  const isDevice = me.actor?.kind === 'device';

  const load = useCallback(async () => {
    const result = await api.household();
    setHousehold(result.household);
    setMembers(result.members);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (patch: Partial<Household>) => {
    setBusy(true);
    try {
      await api.updateHousehold(patch);
      setHousehold((current) => (current ? { ...current, ...patch } : current));
      toast(t.saved);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  if (!household) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="stack">
      <h1>{t.title}</h1>

      <Card title={t.language}>
        <Segmented
          value={language}
          options={[
            { value: 'en', label: 'English' },
            { value: 'fr', label: 'Français' },
          ]}
          onChange={setLanguage}
        />
      </Card>

      {isDevice && <p className="banner">{t.adultOnly}</p>}

      <Card title={t.household}>
        <div className="stack" style={{ gap: 14 }}>
          <div className="field">
            <label htmlFor="hh-name">{t.name}</label>
            <input
              id="hh-name"
              type="text"
              defaultValue={household.name}
              disabled={isDevice || busy}
              onBlur={(event) => event.target.value !== household.name && void save({ name: event.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="hh-location">{t.location}</label>
            <input
              id="hh-location"
              type="text"
              defaultValue={household.location}
              disabled={isDevice || busy}
              onBlur={(event) =>
                event.target.value !== household.location && void save({ location: event.target.value })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="hh-email">{t.email}</label>
            <input
              id="hh-email"
              type="email"
              defaultValue={household.default_email ?? ''}
              disabled={isDevice || busy}
              onBlur={(event) =>
                event.target.value !== household.default_email && void save({ default_email: event.target.value })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="hh-fish">{t.fish}</label>
            <input
              id="hh-fish"
              type="number"
              min={0}
              max={7}
              defaultValue={household.fish_minimum_per_week}
              disabled={isDevice || busy}
              onBlur={(event) => {
                const value = Number(event.target.value);
                if (value !== household.fish_minimum_per_week) void save({ fish_minimum_per_week: value });
              }}
            />
          </div>

          <div className="field">
            <label htmlFor="hh-baking">{t.baking}</label>
            <input
              id="hh-baking"
              type="number"
              min={0}
              max={7}
              defaultValue={household.gouter_baked_per_week}
              disabled={isDevice || busy}
              onBlur={(event) => {
                const value = Number(event.target.value);
                if (value !== household.gouter_baked_per_week) {
                  void save({ gouter_baked_per_week: value });
                }
              }}
            />
            <p className="dim" style={{ fontSize: 13, margin: '4px 0 0' }}>
              {t.bakingHelp}
            </p>
          </div>

          <div className="field">
            <label htmlFor="hh-puddings">{t.puddings}</label>
            <input
              id="hh-puddings"
              type="number"
              min={0}
              max={7}
              defaultValue={household.desserts_made_per_week}
              disabled={isDevice || busy}
              onBlur={(event) => {
                const value = Number(event.target.value);
                if (value !== household.desserts_made_per_week) {
                  void save({ desserts_made_per_week: value });
                }
              }}
            />
            <p className="dim" style={{ fontSize: 13, margin: '4px 0 0' }}>
              {t.puddingsHelp}
            </p>
          </div>

          <label className="row" style={{ gap: 10 }}>
            <input
              type="checkbox"
              style={{ width: 22, height: 22, minHeight: 0 }}
              defaultChecked={household.drinks_alcoholic}
              disabled={isDevice || busy}
              onChange={(event) => void save({ drinks_alcoholic: event.target.checked })}
            />
            <span>
              {t.drinksAlcoholic}
              <br />
              <span className="dim" style={{ fontSize: 13 }}>{t.drinksAlcoholicHelp}</span>
            </span>
          </label>

          <label className="row" style={{ gap: 10 }}>
            <input
              type="checkbox"
              style={{ width: 22, height: 22, minHeight: 0 }}
              defaultChecked={household.auto_email_on_finalise}
              disabled={isDevice || busy}
              onChange={(event) => void save({ auto_email_on_finalise: event.target.checked })}
            />
            <span>{t.autoEmail}</span>
          </label>

          <label className="row" style={{ gap: 10 }}>
            <input
              type="checkbox"
              style={{ width: 22, height: 22, minHeight: 0 }}
              defaultChecked={household.whatsapp_enabled}
              disabled={isDevice || busy}
              onChange={(event) => void save({ whatsapp_enabled: event.target.checked })}
            />
            <span>
              {t.whatsapp}
              <br />
              <span className="dim" style={{ fontSize: 13 }}>{t.whatsappHelp}</span>
            </span>
          </label>

          <label className="row" style={{ gap: 10 }}>
            <input
              type="checkbox"
              style={{ width: 22, height: 22, minHeight: 0 }}
              defaultChecked={household.include_staples}
              disabled={isDevice || busy}
              onChange={(event) => void save({ include_staples: event.target.checked })}
            />
            <span>{t.staples}</span>
          </label>
        </div>
      </Card>

      <Card title={t.people}>
        <p className="dim" style={{ marginBottom: 12 }}>
          {t.renameHelp}
        </p>
        <div className="stack" style={{ gap: 10 }}>
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              t={t}
              readOnly={isDevice}
              onChanged={load}
              onError={(message) => toast(message)}
            />
          ))}

          {!isDevice && (
            <form
              className="row"
              style={{ gap: 8, marginTop: 8 }}
              onSubmit={async (event) => {
                event.preventDefault();
                const form = event.currentTarget;
                const data = new FormData(form);
                await api.addMember({
                  name: String(data.get('name') ?? ''),
                  member_type: data.get('type') as Member['member_type'],
                  portion_factor: Number(data.get('portion') ?? 1),
                });
                form.reset();
                void load();
              }}
            >
              <input name="name" type="text" placeholder="Name" required style={{ flex: 2, minWidth: 140 }} />
              <select name="type" style={{ flex: 1, minWidth: 110 }}>
                <option value="adult">{t.adult}</option>
                <option value="child">{t.child}</option>
                <option value="aupair">{t.aupair}</option>
              </select>
              <input
                name="portion"
                type="number"
                step="0.1"
                min="0.1"
                max="2"
                defaultValue="1"
                style={{ flex: 1, minWidth: 90 }}
              />
              <button className="btn" type="submit">
                {t.add}
              </button>
            </form>
          )}
        </div>
      </Card>

      {/* Access and devices are adult-only, and pointless on the tablet itself. */}
      {!isDevice && <HouseholdAccessCard language={language} currentEmail={me.actor?.email} />}
      {!isDevice && <KitchenDeviceCard language={language} />}

      <DislikesCard language={language} readOnly={isDevice} />

      <ShoppingDaysCard language={language} readOnly={isDevice} />

      <SchoolRunPattern language={language} readOnly={isDevice} />

      {!isDevice && (
        <Card title={t.backup}>
          <p className="dim" style={{ marginBottom: 14 }}>
            {t.backupWhy}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <a className="btn" href={api.exportUrl()} download>
              {t.download}
            </a>
            <label className="btn" style={{ cursor: 'pointer' }}>
              {t.restore}
              <input
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  try {
                    const result = await api.importBackup(JSON.parse(await file.text()));
                    toast(t.restored(result.mealPreferences));
                    void load();
                  } catch (error) {
                    toast(error instanceof Error ? error.message : 'Could not restore that file');
                  } finally {
                    event.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </Card>
      )}

      <Card title={t.account}>
        <p className="muted" style={{ marginBottom: 14 }}>
          {isDevice ? `${t.device} — ${me.actor?.name ?? ''}` : `${t.signedInAs} ${me.actor?.email ?? ''}`}
        </p>
        <button className="btn" onClick={onSignOut}>
          {t.signOut}
        </button>

        {/* Which build this is, so "has it shipped yet?" has an answer that
            does not involve guessing at what changed. */}
        <p className="dim" style={{ fontSize: 13, marginTop: 16 }}>
          {t.version} <code>{version.running}</code>
          {version.stale && (
            <>
              {' · '}
              {t.newerAvailable} <code>{version.latest}</code>{' '}
              <button className="btn small" onClick={() => void reloadToLatest()}>
                {t.reload}
              </button>
            </>
          )}
        </p>
      </Card>
    </div>
  );
}

type Copy = (typeof T)[Language];

/** Today, in the local calendar rather than UTC. */
function today(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function MemberRow({
  member,
  t,
  readOnly,
  onChanged,
  onError,
}: {
  member: Member;
  t: Copy;
  readOnly: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const label =
    member.member_type === 'adult' ? t.adult : member.member_type === 'aupair' ? t.aupair : t.child;
  // Keto is an adult decision — nobody is putting a seven-year-old on it.
  const canKeto = member.member_type !== 'child';
  const ketoOn = member.default_diet_mode === 'keto' || Boolean(member.keto_from || member.keto_until);

  const patch = async (changes: Partial<Member>) => {
    try {
      await api.updateMember(member.id, changes);
      onChanged();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Could not save');
    }
  };

  return (
    <div className="member-row">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          aria-label={`${t.rename} — ${member.name}`}
          defaultValue={member.name}
          disabled={readOnly}
          style={{ flex: 1, minWidth: 120, fontWeight: 550 }}
          onBlur={(event) => {
            const name = event.target.value.trim();
            if (name && name !== member.name) void patch({ name });
            else event.target.value = member.name;
          }}
        />
        <span className="dim">{label}</span>
        <label className="row" style={{ gap: 6 }}>
          <span className="dim">{t.portion}</span>
          <input
            type="number"
            step="0.1"
            min="0.1"
            max="2"
            aria-label={`${t.portion} — ${member.name}`}
            defaultValue={member.portion_factor}
            disabled={readOnly}
            style={{ width: 78 }}
            onBlur={(event) => {
              const value = Number(event.target.value);
              if (value && value !== member.portion_factor) void patch({ portion_factor: value });
            }}
          />
        </label>
        {ketoOn && <span className="pill keto">{t.keto}</span>}
        {!readOnly && canKeto && (
          <button className="btn small ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {t.keto}
          </button>
        )}
        {!readOnly && (
          <button
            className="btn small danger"
            onClick={async () => {
              await api.removeMember(member.id);
              onChanged();
            }}
          >
            {t.remove}
          </button>
        )}
      </div>

      {open && !readOnly && canKeto && (
        <div className="stack" style={{ gap: 10, marginTop: 10 }}>
          <Segmented
            value={ketoOn ? 'keto' : 'normal'}
            options={[
              { value: 'normal', label: t.ketoOff },
              { value: 'keto', label: t.ketoOn },
            ]}
            onChange={(value) =>
              void patch(
                value === 'keto'
                  ? { keto_from: member.keto_from ?? today() }
                  : { default_diet_mode: 'normal', keto_from: null, keto_until: null },
              )
            }
          />
          {ketoOn && (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <label className="field" style={{ flex: 1, minWidth: 140 }}>
                <span className="dim">{t.ketoFrom}</span>
                <input
                  type="date"
                  defaultValue={member.keto_from ?? today()}
                  onChange={(event) => void patch({ keto_from: event.target.value || null })}
                />
              </label>
              <label className="field" style={{ flex: 1, minWidth: 140 }}>
                <span className="dim">{t.ketoUntil}</span>
                <input
                  type="date"
                  defaultValue={member.keto_until ?? ''}
                  onChange={(event) => void patch({ keto_until: event.target.value || null })}
                />
              </label>
            </div>
          )}
          <p className="dim" style={{ fontSize: 13 }}>
            {t.ketoHelp}
          </p>
        </div>
      )}
    </div>
  );
}
